import { AccountBookFilterContainer } from "./AccountBookFilterPanel.styled"
import { FilterTree, type FilterItem } from "../../../../../../components/FilterTree"
import { IAccountBookPairs } from "../../../types"
import useTradingAccountsQuery from "../../hooks/useTradingAccountsQuery"
import { useCallback, useMemo } from "react"
import useBooksQuery from "../../hooks/useBooksQuery"

type AccountBookFilterPanelProps = {
  selectedFilters: IAccountBookPairs
  setSelectedFilters: (next: IAccountBookPairs) => void
}

const AccountBookFilterPanel = ({
  selectedFilters,
  setSelectedFilters,
}: AccountBookFilterPanelProps) => {
  // Fetch full lists to build filter tree even when there are no rows
  const { accounts } = useTradingAccountsQuery({ enabled: true })
  const { books } = useBooksQuery({}, { enabled: true })

  // Build FilterTree: Account -> Books
  const accountBookRecords: FilterItem[] = useMemo(() => {
    // Prefer full list from accounts/books so filters are available even with no rows
    if ((accounts?.length ?? 0) > 0 && (books?.length ?? 0) > 0) {
      const records = accounts.map(acc => {
        const accBooks = books
          .filter(b => (b.trading_account_scients ?? []).some(a => a.id === acc.id))
          .map(b => ({ id: b.code, label: b.code }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))
        return { id: `${acc.name}` as string, label: acc.name, children: accBooks }
      })
      // Keep only accounts that have at least one book
      const withChildren = records.filter(item => (item.children?.length ?? 0) > 0)
      if (withChildren.length > 0) return withChildren
    }

    return []
  }, [accounts, books])

  // Adapt IAccountBookPairs -> FilterTree selected shape
  const treeSelectedFilters = useMemo<Record<string, Array<string | number>>>(() => {
    if (!accounts || !books) return {}

    const accountIdToName = new Map(accounts.map(acc => [acc.id, acc.name]))
    const bookIdToCode = new Map(books.map(b => [b.id, b.code]))

    const result: Record<string, Array<string | number>> = {}
    for (const pair of selectedFilters ?? []) {
      const accountName = accountIdToName.get(pair.account_id)
      if (!accountName) continue
      const values = (pair.book_ids ?? [])
        .map(bookId => bookIdToCode.get(bookId))
        .filter((v): v is string => Boolean(v))
      if (values.length > 0) {
        result[accountName] = values
      }
    }
    return result
  }, [accounts, books, selectedFilters])

  // Adapt FilterTree selected shape -> IAccountBookPairs
  const handleTreeSelectedChange = useCallback(
    (next: Record<string, Array<string | number>>) => {
      if (!accounts || !books) {
        setSelectedFilters([])
        return
      }

      const accountNameToId = new Map(accounts.map(acc => [acc.name, acc.id]))
      const bookCodeToId = new Map(books.map(b => [b.code, b.id]))

      const outPairs: IAccountBookPairs = []
      for (const [accountName, valueIds] of Object.entries(next)) {
        const accountId = accountNameToId.get(accountName)
        if (!accountId) continue
        const bookIds = valueIds
          .map(v => bookCodeToId.get(String(v)))
          .filter((v): v is number => typeof v === "number")
        if (bookIds.length > 0) {
          outPairs.push({ account_id: accountId, book_ids: bookIds })
        }
      }
      setSelectedFilters(outPairs)
    },
    [accounts, books, setSelectedFilters],
  )

  return (
    <AccountBookFilterContainer flexDirection="column">
      <FilterTree
        data={accountBookRecords}
        selectedFilters={treeSelectedFilters}
        onSelectedFilterChange={handleTreeSelectedChange}
      />
    </AccountBookFilterContainer>
  )
}

export default AccountBookFilterPanel
